/* eslint-disable react/no-unknown-property */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Color } from 'three';
import { OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import AnimationController from './AnimationController';
import HandStuffedContainer from './HandStuffedContainer';
import ViewportControls from './ViewportControls';
import CameraSync from './CameraSync';
import ViewOffset from './ViewOffset';
import { applyCameraTransform } from './cameraTransform';
import { ScrollableList } from '../../shared/ScrollableList';
import { HoverTooltip } from '../../shared/HoverTooltip';
import { logEvent } from '../../../utils/analytics';

const DIVISOR = 2;

// cameraZoom/cameraRotationDeg/cameraTiltDeg/cameraDistanceOverride default
// to a no-op — only Beyond CBM passes non-default values; the console
// (SimulationsTab) calls this with none of them, so its framing is
// byte-for-byte unchanged. showPackageFilter/showLegend are the same kind of
// opt-in switch: both default false, so console gets neither the "All
// Packages" dropdown nor the legend panel unless it's explicitly asked for.
const HandStuffedModel = ({ entities, packedContainers, cameraZoom = 1, cameraRotationDeg = 0, cameraTiltDeg = 0, cameraDistanceOverride, cameraPanRight = 0, edgeColor = 'red', canvasHeight = 560, showPackageFilter = false, showLegend = false }) => {
  const controlsRef = useRef(null);
  const [containerFilter, setContainerFilter] = useState('ALL');
  const [packageFilter, setPackageFilter] = useState('ALL');
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  // Index into containersFiltered, not container id — two packedContainer
  // entries can share the same id (same type, multiple physical copies), so
  // an id-based highlight would light up every copy at once instead of just
  // the one row being hovered.
  const [hoveredContainerIdx, setHoveredContainerIdx] = useState(null);
  // In-page "large view" — not the browser Fullscreen API (that's a jarring
  // takeover, and unavailable inside some embedded contexts). Portaled to
  // document.body so it always sits above every ancestor's stacking context
  // regardless of transforms/overflow further up the tree, same reasoning as
  // HoverTooltip's portal use.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [expanded]);

  // Options come from packedContainers (what's actually in the result), not
  // entities.containers (every type the user input) — an input type the
  // optimizer didn't end up using would otherwise show up as a filter option
  // that filters to nothing.
  const containerSelectOptions = useMemo(() => {
    const opts = [{ label: 'All Containers', value: 'ALL' }];
    const seen = new Set();
    packedContainers.forEach((c) => {
      if (!seen.has(c.id)) { seen.add(c.id); opts.push({ label: c.id, value: c.id }); }
    });
    return opts;
  }, [packedContainers]);

  // Same sourcing logic as containerSelectOptions, but from each container's
  // packed item stats — independent of the container filter, so switching
  // container selection never makes a package option disappear out from
  // under the user.
  const packageSelectOptions = useMemo(() => {
    const opts = [{ label: 'All Packages', value: 'ALL' }];
    const seen = new Set();
    packedContainers.forEach((c) => {
      (c.stats?.itemDetails || []).forEach((item) => {
        if (!seen.has(item.id)) { seen.add(item.id); opts.push({ label: item.id, value: item.id }); }
      });
    });
    return opts;
  }, [packedContainers]);

  const containersFiltered = useMemo(() => {
    setAnimationProgress(0);
    setIsAnimating(false);
    if (containerFilter === 'ALL') return packedContainers;
    return packedContainers.filter((c) => c.id === containerFilter);
  }, [packedContainers, containerFilter]);

  // Legend data — always scoped to containersFiltered (respects the
  // container dropdown) and, for packages, also to packageFilter, so the
  // legend only ever lists what's actually visible in the arena right now.
  const legendContainers = useMemo(() => {
    return containersFiltered.map((c) => {
      const key = Object.keys(entities.containers).find((k) => k === c.id);
      // Distinct package colors packed inside this specific container
      // instance — deduped by item id (a Map, keyed on id, naturally
      // collapses repeats) and filtered the same way the boxes themselves
      // are, so the swatch list always matches what's actually rendered.
      // Quantity here is per single physical copy of this container, not
      // multiplied by c.quantity — this is "what's in one of these", not a
      // running total (the aggregate Packages section below covers that).
      const packages = Array.from(
        new Map(
          (c.stats?.itemDetails || [])
            .filter((item) => packageFilter === 'ALL' || item.id === packageFilter)
            .map((item) => [item.id, { id: item.id, quantity: item.quantity, color: entities.items[item.id]?.color || 'blue' }])
        ).values()
      );
      return {
        id: c.id,
        quantity: c.quantity,
        dims: key ? entities.containers[key] : null,
        weightUtil: c.stats?.weight?.utilisation,
        volumeUtil: c.stats?.volume?.utilisation,
        packages,
      };
    });
  }, [containersFiltered, entities, packageFilter]);

  const legendPackages = useMemo(() => {
    // Rotated = actually placed in the non-default orientation somewhere in
    // this result (orientation.length === 'width'), not just "allowed to
    // rotate" per possibleOrientations — an item can have 2 possible
    // orientations and still never end up rotated if the identity
    // orientation packed just as well everywhere.
    const rotatedIds = new Set();
    containersFiltered.forEach((c) => {
      (c.stacks || []).forEach((stack) => {
        stack.forEach((entry) => {
          if (entry.orientation?.length === 'width') rotatedIds.add(entry.item);
        });
      });
    });

    const map = new Map();
    containersFiltered.forEach((c) => {
      // itemDetails[].quantity is per single physical copy of this
      // container, not a total — a packedContainer entry with c.quantity: 13
      // means 13 identical copies, each holding that many items. Verified
      // live: quantity * c.quantity, summed across entries, matches the
      // requested input total exactly; the raw un-multiplied sum badly
      // undercounts whenever a container type needs more than one copy.
      const containerQty = c.quantity || 1;
      (c.stats?.itemDetails || []).forEach((item) => {
        if (packageFilter !== 'ALL' && item.id !== packageFilter) return;
        const addQty = item.quantity * containerQty;
        const existing = map.get(item.id);
        if (existing) { existing.quantity += addQty; return; }
        const details = entities.items[item.id];
        map.set(item.id, { id: item.id, quantity: addQty, color: details?.color || 'blue', dims: details, rotated: rotatedIds.has(item.id) });
      });
    });
    return Array.from(map.values());
  }, [containersFiltered, entities, packageFilter]);

  const totalWidth = useMemo(() => {
    const cum = containersFiltered.reduce((acc, c, i) => {
      const key = Object.keys(entities.containers).find((k) => k === c.id);
      if (!key) return acc;
      const w = entities.containers[key].width;
      return acc + w + (i === containersFiltered.length - 1 ? 0 : DIVISOR);
    }, 0);
    return cum / DIVISOR;
  }, [containersFiltered, entities]);

  const maxContainerHeight = Math.max(
    ...containersFiltered.map((c) => {
      const key = Object.keys(entities.containers).find((k) => k === c.id);
      return key ? entities.containers[key].height / DIVISOR : 0;
    })
  );

  const maxContainerLength = Math.max(
    ...containersFiltered.map((c) => {
      const key = Object.keys(entities.containers).find((k) => k === c.id);
      return key ? entities.containers[key].length / DIVISOR : 0;
    })
  );

  const minContainerWidth = useMemo(() => {
    const vals = containersFiltered.map((c) => {
      const key = Object.keys(entities.containers).find((k) => k === c.id);
      return key ? entities.containers[key].width / DIVISOR : 0;
    });
    return vals.length ? Math.min(...vals) : 0;
  }, [containersFiltered, entities]);

  let currentZ = -totalWidth * 0.9;
  const getZValue = (idx) => {
    const c = containersFiltered[idx];
    if (!c) return 0;
    const key = Object.keys(entities.containers).find((k) => k === c.id);
    const w = key ? entities.containers[key].width / DIVISOR : 0;
    const z = currentZ;
    currentZ += w + minContainerWidth;
    return z;
  };

  // Framed off the full packedContainers set, not containersFiltered — using
  // the filtered subset meant picking a different container in the top-left
  // selector recomputed totalWidth/etc, which recomputed cameraPosition, which
  // CameraSync then force-applied, snapping away any manual orbit/zoom the
  // user had just set up. Framing off the unfiltered set keeps cameraPosition
  // stable across filter changes so CameraSync has nothing new to sync.
  const cameraTotalWidth = useMemo(() => {
    const cum = packedContainers.reduce((acc, c, i) => {
      const key = Object.keys(entities.containers).find((k) => k === c.id);
      if (!key) return acc;
      const w = entities.containers[key].width;
      return acc + w + (i === packedContainers.length - 1 ? 0 : DIVISOR);
    }, 0);
    return cum / DIVISOR;
  }, [packedContainers, entities]);

  const cameraMaxContainerHeight = Math.max(
    0,
    ...packedContainers.map((c) => {
      const key = Object.keys(entities.containers).find((k) => k === c.id);
      return key ? entities.containers[key].height / DIVISOR : 0;
    })
  );

  const cameraMaxContainerLength = Math.max(
    0,
    ...packedContainers.map((c) => {
      const key = Object.keys(entities.containers).find((k) => k === c.id);
      return key ? entities.containers[key].length / DIVISOR : 0;
    })
  );

  // Beyond CBM passes cameraDistanceOverride: the exact same distance number
  // its pre-run arena preview computed for these picks, so results use that
  // literal value instead of a distance re-derived from this component's own
  // totalWidth (which reflects the result's actual data shape and isn't
  // guaranteed to match what the preview assumed). Console never passes an
  // override, so it always falls through to its original formula below.
  const cameraPosition = useMemo(() => {
    const isDefaultCamera = cameraZoom === 1 && cameraRotationDeg === 0 && cameraTiltDeg === 0;
    if (isDefaultCamera) {
      // Untouched console path — exact original formula, byte-for-byte.
      return !containersFiltered.length ? [0, 20, 50] : [40, 10, Math.max(totalWidth, maxContainerLength, maxContainerHeight) * 1.5];
    }
    if (!packedContainers.length) return [0, 20, 50];
    const distance = cameraDistanceOverride ?? Math.max(cameraTotalWidth, cameraMaxContainerLength, cameraMaxContainerHeight) * 1.5;
    return applyCameraTransform(distance, { zoom: cameraZoom, rotationDeg: cameraRotationDeg, tiltDeg: cameraTiltDeg });
  }, [containersFiltered, totalWidth, maxContainerLength, maxContainerHeight, packedContainers, cameraTotalWidth, cameraMaxContainerLength, cameraMaxContainerHeight, cameraDistanceOverride, cameraZoom, cameraRotationDeg, cameraTiltDeg]);

  const maxBoxCount = useMemo(() => {
    if (!containersFiltered.length) return 0;
    const result = containersFiltered.reduce((acc, c) => {
      return Math.max(acc, (c.stacks || []).reduce((s, stack) => s + (stack[0]?.quantity || 0), 0));
    }, 0);
    setAnimationProgress(result);
    return result;
  }, [containersFiltered]);

  const handlePlay = () => {
    if (animationProgress >= maxBoxCount) setAnimationProgress(0);
    setIsAnimating(true);
  };

  const arena = (
    <div className={expanded ? 'relative h-full w-full' : 'relative w-full'} style={expanded ? undefined : { height: canvasHeight }}>
      {/* Canvas clipped independently so controls are never cut */}
      <div className="absolute inset-0 rounded-xl overflow-hidden">
        <Canvas
          style={{ width: '100%', height: '100%' }}
          scene={{ background: new Color(0x000000) }}
          camera={{ position: cameraPosition, fov: 50, near: 1, far: 1000 }}
        >
          <AnimationController
            isAnimating={isAnimating}
            setIsAnimating={setIsAnimating}
            animationProgress={animationProgress}
            setAnimationProgress={setAnimationProgress}
            maxAnimationProgress={maxBoxCount}
          />
          <gridHelper args={[1000, 120, 0xffffff, 0xffffff]} material-opacity={0.25} material-transparent material-linewidth={1.0} position={[0, 0, 0]} />
          <ambientLight intensity={0.7} color={0xffffff} />
          <directionalLight position={[50, 50, 50]} intensity={0.8} color={0xffffff} castShadow />
          <directionalLight position={[0, -50, 0]} intensity={0.6} color={0xffffff} />
          <OrbitControls ref={controlsRef} makeDefault />
          <CameraSync position={cameraPosition} controlsRef={controlsRef} />
          <ViewOffset rightRatio={cameraPanRight} />
          {containersFiltered.map((c, i) => (
            <HandStuffedContainer
              key={`hs-${i}`}
              container={c}
              entities={entities}
              zValue={getZValue(i)}
              animationProgress={animationProgress}
              edgeColor={edgeColor}
              packageFilter={packageFilter}
              highlighted={i === hoveredContainerIdx}
            />
          ))}
        </Canvas>
      </div>

      {/* Container/package selectors — top-left */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
        <select
          className="rounded-md border border-gray-600 bg-gray-900 py-1.5 pl-3 pr-8 text-sm text-white focus:border-primary focus:outline-none"
          value={containerFilter}
          onChange={(e) => setContainerFilter(e.target.value)}
        >
          {containerSelectOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {showPackageFilter && (
          <select
            className="rounded-md border border-gray-600 bg-gray-900 py-1.5 pl-3 pr-8 text-sm text-white focus:border-primary focus:outline-none"
            value={packageFilter}
            onChange={(e) => setPackageFilter(e.target.value)}
          >
            {packageSelectOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
        {/* Expand/collapse lives in this same flex row (not its own
            top-right box) — ViewportControls already occupies top-3 right-3,
            so a second independently-positioned corner element there would
            either overlap it or need fragile magic-number offsets. */}
        <button
          onClick={() => {
            if (!expanded) logEvent({ name: 'arena_expand', type: 'engagement' });
            setExpanded((v) => !v);
          }}
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md border border-gray-600 bg-gray-900 text-slate-300 transition hover:border-primary hover:text-white"
          title={expanded ? 'Exit expanded view' : 'Expand view'}
        >
          <span className="material-symbols-outlined text-[18px]">{expanded ? 'close_fullscreen' : 'open_in_full'}</span>
        </button>
      </div>

      {showLegend && (legendContainers.length > 0 || legendPackages.length > 0) && (
        <div className="absolute top-16 left-3 z-10 max-w-[250px] space-y-3 rounded-lg border border-white/10 bg-black/60 p-3 text-xs text-white backdrop-blur-sm">
          {legendContainers.length > 0 && (
            <div>
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">Containers</p>
              <ScrollableList maxHeightClass="max-h-44" innerClassName="space-y-2">
                {legendContainers.map((c, idx) => (
                  <div
                    key={`${c.id}-${idx}`}
                    onMouseEnter={() => setHoveredContainerIdx(idx)}
                    onMouseLeave={() => setHoveredContainerIdx(null)}
                    className={`-mx-1 cursor-default rounded px-1 py-0.5 transition-colors ${idx === hoveredContainerIdx ? 'bg-white/10' : ''}`}
                  >
                    <p className="font-semibold leading-tight">{c.id} <span className="font-normal text-slate-500">×{c.quantity}</span></p>
                    {(c.volumeUtil != null || c.weightUtil != null) && (
                      <p className="text-slate-400">
                        {c.volumeUtil != null && `Vol ${c.volumeUtil}%`}
                        {c.volumeUtil != null && c.weightUtil != null && ' · '}
                        {c.weightUtil != null && `Wt ${c.weightUtil}%`}
                      </p>
                    )}
                    {c.packages.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {c.packages.map((p) => (
                          <span key={p.id} title={`${p.id} ×${p.quantity}`} className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: p.color }} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </ScrollableList>
            </div>
          )}
          {legendPackages.length > 0 && (
            <div>
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400">Packages</p>
              <ScrollableList maxHeightClass="max-h-36" innerClassName="space-y-2">
                {legendPackages.map((p) => (
                  <div key={p.id} className="flex items-start gap-1.5">
                    <span className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: p.color }} />
                    <div>
                      <p className="mb-1.5 font-semibold leading-tight">
                        {p.id} <span className="font-normal text-slate-500">×{p.quantity}</span>
                        {p.rotated && (
                          <HoverTooltip icon="3d_rotation" iconClassName="ml-1 text-[13px] text-primary/80">
                            Rotated 90° here — the engine found this orientation packed tightest.
                          </HoverTooltip>
                        )}
                      </p>
                      {p.dims && (
                        <p className="text-slate-400">{p.dims.length}×{p.dims.width}×{p.dims.height}m</p>
                      )}
                    </div>
                  </div>
                ))}
              </ScrollableList>
            </div>
          )}
        </div>
      )}

      <ViewportControls controlsRef={controlsRef} />

      {/* Animation controls — full-width bottom bar, outside the clip zone */}
      {containersFiltered.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center gap-3 bg-gradient-to-t from-black/80 to-transparent px-4 pb-4 pt-8">
          {isAnimating ? (
            <button
              onClick={() => setIsAnimating(false)}
              className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary hover:bg-primary/30 transition"
              title="Pause"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            </button>
          ) : (
            <button
              onClick={handlePlay}
              className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary hover:bg-primary/30 transition"
              title="Play"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
            </button>
          )}
          <input
            type="range"
            min={0}
            max={maxBoxCount}
            value={animationProgress}
            onChange={(e) => { setIsAnimating(false); setAnimationProgress(Number(e.target.value)); }}
            className="flex-1 accent-primary"
          />
          <button
            onClick={() => { setIsAnimating(false); setAnimationProgress(0); }}
            className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-slate-400 hover:bg-white/20 hover:text-white transition"
            title="Reset"
          >
            <span className="material-symbols-outlined text-[18px]">refresh</span>
          </button>
        </div>
      )}
    </div>
  );

  if (!expanded) return arena;

  // Backdrop click closes; the arena's own click is stopped from bubbling so
  // orbiting/clicking inside it doesn't dismiss the overlay.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm md:p-10"
      onClick={() => setExpanded(false)}
    >
      <div className="h-full w-full max-w-[1600px]" onClick={(e) => e.stopPropagation()}>
        {arena}
      </div>
    </div>,
    document.body
  );
};

export default HandStuffedModel;
